/**
 * Offline-readiness helpers. Everything here is best-effort and
 * swallows its own failures — missing network is never a hard error.
 *
 * Strategy:
 *   - "prefetch" = fire-and-forget Image() loads; service worker's
 *     CacheFirst rule for Deezer CDN will retain them for 60 days.
 *   - "download for offline" = all of the above, tracked in
 *     localStorage so the UI can show a ✓ per event.
 */
import type { ArtistDTO, EventDTO } from "./api";

const LS_KEY = "checkthelineup:offline:events:v1";

type OfflineRecord = {
  status: "ready" | "partial" | "downloading";
  total: number;
  ok: number;
  at: number;
};

type OfflineMap = Record<string, OfflineRecord>;

function readMap(): OfflineMap {
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? "{}") as OfflineMap;
  } catch {
    return {};
  }
}

function writeMap(m: OfflineMap): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(m));
  } catch { /* quota exceeded or disabled — nothing to do */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:changed"));
  }
}

export function getOfflineStatus(eventSlug: string): OfflineRecord | null {
  return readMap()[eventSlug] ?? null;
}

export function listOfflineStatus(): OfflineMap {
  return readMap();
}

/**
 * Fire all photo URLs through the browser's image cache in the background.
 * The SW's deezer-photos rule (CacheFirst, 60-day TTL) retains them.
 * Returns a promise that resolves once every image has loaded or errored.
 */
export async function prefetchPhotos(urls: string[]): Promise<{ total: number; ok: number }> {
  const real = urls.filter(Boolean);
  let ok = 0;
  const tasks = real.map(
    url =>
      new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => { ok++; resolve(); };
        img.onerror = () => resolve();
        img.src = url;
      }),
  );
  await Promise.all(tasks);
  return { total: real.length, ok };
}

/**
 * Lightweight prefetch: fire-and-forget, no tracking, no state changes.
 * Called opportunistically (e.g. when the user taps an event card).
 */
export function prefetchPhotosBackground(urls: string[]): void {
  for (const url of urls) {
    if (!url) continue;
    const img = new Image();
    img.src = url;
  }
}

/**
 * Full-fat "Download for offline" — caches every photo for an event,
 * tracks progress, and persists a ready/partial marker in localStorage
 * so the event card can render a ✓.
 */
export async function downloadForOffline(
  event: EventDTO,
  artists: ArtistDTO[],
  onProgress?: (done: number, total: number) => void,
): Promise<OfflineRecord> {
  const urls = artists.map(a => a.photoUrl).filter((u): u is string => !!u);
  const map = readMap();
  map[event.slug] = { status: "downloading", total: urls.length, ok: 0, at: Date.now() };
  writeMap(map);

  let ok = 0;
  let done = 0;
  for (const url of urls) {
    await new Promise<void>(resolve => {
      const img = new Image();
      img.onload = () => { ok++; done++; resolve(); };
      img.onerror = () => { done++; resolve(); };
      img.src = url;
    });
    onProgress?.(done, urls.length);
    // Refresh progress in storage every ~10 images so the UI ticks.
    if (done % 10 === 0) {
      const m = readMap();
      m[event.slug] = { status: "downloading", total: urls.length, ok, at: Date.now() };
      writeMap(m);
    }
  }

  const final: OfflineRecord = {
    status: ok === urls.length ? "ready" : ok > urls.length * 0.8 ? "ready" : "partial",
    total: urls.length,
    ok,
    at: Date.now(),
  };
  const m = readMap();
  m[event.slug] = final;
  writeMap(m);
  return final;
}

export function clearOfflineStatus(eventSlug?: string): void {
  const m = readMap();
  if (eventSlug) delete m[eventSlug];
  else Object.keys(m).forEach(k => delete m[k]);
  writeMap(m);
}
